import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { uploadFileToObjectStorage } from "@/lib/object-storage-upload";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Film, FileText, Video, Image as ImageIcon, Search, Calendar as CalendarIcon, Grid, List, Edit, Trash2, X, Link as LinkIcon, OctagonAlert, Share2, User as UserIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import ShareDialog from "@/components/share-dialog";
import type { MediaFile, Goal, MediaShare, User, MediaShareWithOwner } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { isFutureGoal } from "@/lib/goal-visibility";

const uploadFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  category: z.string().min(1, "Category is required"),
  tags: z.string().optional(),
  file: z.any().refine((file) => file !== null, "File is required"),
});

const editFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  category: z.string().min(1, "Category is required"),
  tags: z.string().optional(),
  goalId: z.string().optional().nullable(),
});

type UploadFormData = z.infer<typeof uploadFormSchema>;
type EditFormData = z.infer<typeof editFormSchema>;

// Extended type for media files that may or may not have fileUrl
type MediaFileMetadata = Omit<MediaFile, 'fileUrl'> & { 
  fileUrl?: string; 
  hasFileUrl?: boolean; 
};

// Component to lazy-load media thumbnails on-demand
function MediaThumbnail({ 
  mediaId, 
  fileType, 
  name, 
  className = "w-12 h-12 object-cover rounded"
}: { 
  mediaId: string; 
  fileType: string; 
  name: string;
  className?: string;
}) {
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  
  const isImage = fileType === "jpeg" || fileType === "jpg" || fileType === "png";
  
  // Load thumbnail on mount for images
  useEffect(() => {
    if (isImage && !fileUrl && !isLoading && !error) {
      setIsLoading(true);
      fetch(`/api/media/${mediaId}/content`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : Promise.reject())
        .then(data => {
          setFileUrl(data.fileUrl);
          setIsLoading(false);
        })
        .catch(() => {
          setError(true);
          setIsLoading(false);
        });
    }
  }, [mediaId, isImage]);
  
  if (!isImage) {
    return (
      <div className="w-12 h-12 flex items-center justify-center bg-muted rounded">
        {fileType === "pdf" ? (
          <FileText className="w-5 h-5" />
        ) : fileType === "mp4" || fileType === "mpeg" ? (
          <Video className="w-5 h-5" />
        ) : (
          <Film className="w-5 h-5" />
        )}
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="w-12 h-12 flex items-center justify-center bg-muted rounded animate-pulse">
        <ImageIcon className="w-5 h-5 text-muted-foreground" />
      </div>
    );
  }
  
  if (error || !fileUrl) {
    return (
      <div className="w-12 h-12 flex items-center justify-center bg-muted rounded">
        <ImageIcon className="w-5 h-5 text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <img 
      src={fileUrl} 
      alt={name} 
      className={className}
      data-testid={`img-thumbnail-${mediaId}`}
    />
  );
}

export default function Media() {
  const [viewMode, setViewMode] = useState<"list" | "card">("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [fileTypeFilter, setFileTypeFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [editingMedia, setEditingMedia] = useState<MediaFile | null>(null);
  const [deletingMediaId, setDeletingMediaId] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isStorageWarningOpen, setIsStorageWarningOpen] = useState(false);
  const [sharingMedia, setSharingMedia] = useState<MediaFile | null>(null);
  const [activeTab, setActiveTab] = useState<string>("my-files");
  const [friendSearchQuery, setFriendSearchQuery] = useState("");
  const { toast } = useToast();

  // Helper function for flexible friend search
  function matchesSearch(searchTerm: string, firstName: string, lastName: string, email: string): boolean {
    if (!searchTerm.trim()) return true;
    
    const search = searchTerm.toLowerCase().trim();
    const first = (firstName || '').toLowerCase();
    const last = (lastName || '').toLowerCase();
    const fullName = `${first} ${last}`;
    const combinedName = `${first}${last}`;
    const emailLower = (email || '').toLowerCase();
    
    if (first.includes(search) || last.includes(search) || fullName.includes(search) || emailLower.includes(search)) {
      return true;
    }
    
    if (combinedName.includes(search)) {
      return true;
    }
    
    return combinedName.includes(search);
  }

  const { data: mediaFiles = [], isLoading, isError, error, refetch } = useQuery<MediaFile[]>({
    queryKey: ["/api/media"],
    staleTime: 0, // Always refetch on mount to get fresh data
    refetchOnMount: true,
  });

  // Debug logging for production issues
  console.log('📁 Media query state:', { 
    filesCount: mediaFiles.length, 
    isLoading, 
    isError, 
    error: error?.message 
  });

  const { data: goals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const { data: users = [] } = useQuery<User[]>({
    queryKey: ["/api/users"],
  });

  const { data: sharedWithMe = [], isLoading: isLoadingShared } = useQuery<MediaShareWithOwner[]>({
    queryKey: ["/api/media/shared-with-me"],
  });

  const { data: currentFileShares = [], isLoading: isLoadingShares } = useQuery<MediaShare[]>({
    queryKey: ["/api/media", sharingMedia?.id, "shares"],
    enabled: !!sharingMedia?.id,
  });

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    return users.filter(user => matchesSearch(
      friendSearchQuery,
      user.firstName || '',
      user.lastName || '',
      user.email || ''
    ));
  }, [users, friendSearchQuery]);

  const uploadForm = useForm<UploadFormData>({
    resolver: zodResolver(uploadFormSchema),
    defaultValues: {
      name: "",
      category: "Uncategorized",
      tags: "",
      file: null,
    },
  });

  const editForm = useForm<EditFormData>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      name: "",
      category: "",
      tags: "",
      goalId: null,
    },
  });

  const createMediaMutation = useMutation({
    mutationFn: async (data: any) => {
      return await apiRequest("POST", "/api/media", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/media"] });
      uploadForm.reset();
      setFileInputKey(prev => prev + 1);
    },
    onError: (error: any) => {
      // Error handled silently
    },
  });

  const updateMediaMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return await apiRequest("PATCH", `/api/media/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/media"] });
      setEditingMedia(null);
    },
    onError: (error: any) => {
      // Error handled silently
    },
  });

  const deleteMediaMutation = useMutation({
    mutationFn: async (id: string) => {
      return await apiRequest("DELETE", `/api/media/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/media"] });
      setDeletingMediaId(null);
    },
    onError: (error: any) => {
      // Error handled silently
    },
  });

  const shareMediaMutation = useMutation({
    mutationFn: async ({ mediaId, sharedWithId }: { mediaId: string; sharedWithId: string }) => {
      return await apiRequest("POST", `/api/media/${mediaId}/share`, { sharedWithId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/media", sharingMedia?.id, "shares"] });
      toast({
        title: "File Shared",
        description: "File has been shared successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Share Failed",
        description: error.message || "Could not share file",
        variant: "destructive",
      });
    },
  });

  const unshareMediaMutation = useMutation({
    mutationFn: async (shareId: string) => {
      return await apiRequest("DELETE", `/api/media/shares/${shareId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/media", sharingMedia?.id, "shares"] });
      toast({
        title: "Share Removed",
        description: "File share has been removed",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Unshare Failed",
        description: error.message || "Could not remove share",
        variant: "destructive",
      });
    },
  });

  const onUploadSubmit = async (data: UploadFormData) => {
    const file = data.file;
    if (!file) return;

    const tags = data.tags ? data.tags.split(",").map(t => t.trim()).filter(Boolean) : [];

    try {
      // Upload the file to object storage; the DB only stores the served URL,
      // keeping large files (e.g. videos) out of the database.
      const fileUrl = await uploadFileToObjectStorage(file);

      createMediaMutation.mutate({
        name: data.name,
        fileName: file.name,
        fileUrl,
        fileType: file.type.split("/")[1] || "unknown",
        fileSize: file.size,
        category: data.category,
        tags,
      });
    } catch (error: any) {
      toast({
        title: "Upload Failed",
        description: error?.message || "Could not upload file",
        variant: "destructive",
      });
    }
  };

  const onEditSubmit = (data: EditFormData) => {
    if (!editingMedia) return;
    
    const tags = data.tags ? data.tags.split(",").map(t => t.trim()).filter(Boolean) : [];
    updateMediaMutation.mutate({
      id: editingMedia.id,
      data: {
        name: data.name,
        category: data.category,
        tags,
        goalId: data.goalId || null,
      },
    });
  };

  const openEditDialog = (media: MediaFile) => {
    setEditingMedia(media);
    editForm.reset({
      name: media.name,
      category: media.category,
      tags: media.tags.join(", "),
      goalId: media.goalId,
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const target = e.currentTarget as HTMLElement;
    const relatedTarget = e.relatedTarget as HTMLElement;
    
    if (!relatedTarget || !target.contains(relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    
    const isValidFile = (file: File): boolean => {
      const validMimeTypes = ['image/jpeg', 'image/png', 'application/pdf', 'video/mpeg', 'video/mp4'];
      const validExtensions = ['.jpg', '.jpeg', '.png', '.pdf', '.mpeg', '.mp4'];
      
      if (validMimeTypes.includes(file.type)) {
        return true;
      }
      
      if (!file.type || file.type === '') {
        const extension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        return validExtensions.includes(extension);
      }
      
      return false;
    };

    const validFile = files.find(isValidFile);

    if (validFile) {
      const maxSize = 50 * 1024 * 1024;
      if (validFile.size > maxSize) {
        toast({
          title: "File too large",
          description: `File size must be less than 50MB. Your file is ${(validFile.size / (1024 * 1024)).toFixed(1)}MB.`,
          variant: "destructive",
        });
        return;
      }

      uploadForm.setValue('file', validFile);
      if (!uploadForm.getValues('name')) {
        uploadForm.setValue('name', validFile.name.replace(/\.[^/.]+$/, ''));
      }
      
      toast({
        title: "File ready",
        description: `${validFile.name} is ready to upload. Fill in the details and click Upload.`,
      });
    } else {
      toast({
        title: "Invalid file type",
        description: "Please drop a JPEG, PNG, PDF, or MP4/MPEG video file.",
        variant: "destructive",
      });
    }
  };

  const filteredMedia = useMemo(() => {
    return mediaFiles.filter((media) => {
      if (searchTerm && !media.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      if (fileTypeFilter !== "all" && media.fileType !== fileTypeFilter) {
        return false;
      }
      if (categoryFilter !== "all" && media.category !== categoryFilter) {
        return false;
      }
      if (tagFilter && !media.tags.some(tag => tag.toLowerCase().includes(tagFilter.toLowerCase()))) {
        return false;
      }
      if (dateFrom && new Date(media.createdAt) < dateFrom) {
        return false;
      }
      if (dateTo && new Date(media.createdAt) > dateTo) {
        return false;
      }
      return true;
    });
  }, [mediaFiles, searchTerm, fileTypeFilter, categoryFilter, tagFilter, dateFrom, dateTo]);

  const categories = useMemo(() => {
    const cats = new Set(mediaFiles.map(m => m.category));
    return Array.from(cats);
  }, [mediaFiles]);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType: string) => {
    if (fileType === "jpeg" || fileType === "jpg" || fileType === "png") {
      return <ImageIcon className="w-5 h-5" data-testid="icon-image" />;
    }
    if (fileType === "pdf") {
      return <FileText className="w-5 h-5" data-testid="icon-pdf" />;
    }
    if (fileType === "mp4" || fileType === "mpeg") {
      return <Video className="w-5 h-5" data-testid="icon-video" />;
    }
    return <Film className="w-5 h-5" data-testid="icon-file" />;
  };

  const isImage = (fileType: string) => {
    return fileType === "jpeg" || fileType === "jpg" || fileType === "png";
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-page-title">Media</h1>
          <p className="text-muted-foreground">Upload and manage your media files in App Storage</p>
          <nav className="mt-3 flex items-center gap-2" aria-label="Storage source">
            <Button variant="default" size="sm" asChild data-testid="link-app-storage">
              <Link href="/media">App Storage</Link>
            </Button>
            <Button variant="outline" size="sm" asChild data-testid="link-google-drive">
              <Link href="/drive">Google Drive</Link>
            </Button>
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            className="flex flex-col items-center gap-1 hover:bg-transparent"
            onClick={() => setIsStorageWarningOpen(true)}
            data-testid="button-storage-warning"
          >
            <OctagonAlert className="w-8 h-8 text-red-600" />
            <span className="text-xs text-red-600 font-semibold">Read About Limits</span>
          </Button>
          <div className="flex gap-2">
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="icon"
              onClick={() => setViewMode("list")}
              data-testid="button-view-list"
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "card" ? "default" : "outline"}
              size="icon"
              onClick={() => setViewMode("card")}
              data-testid="button-view-card"
            >
              <Grid className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Upload Form */}
      <Card 
        data-testid="card-upload-form"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={isDragging ? "border-primary border-2 bg-primary/5" : ""}
      >
        <CardHeader>
          <CardTitle>Upload Media</CardTitle>
          <CardDescription style={{ fontSize: '18px', color: '#ff0000' }}>
            {isDragging 
              ? "Drop your file here" 
              : "DRAG AND DROP FILE HERE or simply find the file and then upload"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...uploadForm}>
            <form onSubmit={uploadForm.handleSubmit(onUploadSubmit)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={uploadForm.control}
                  name="file"
                  render={({ field: { onChange, value, ...field } }) => (
                    <FormItem>
                      <FormLabel>File</FormLabel>
                      <FormControl>
                        <Input
                          key={fileInputKey}
                          type="file"
                          accept=".jpeg,.jpg,.png,.pdf,.mpeg,.mp4"
                          onChange={(e) => onChange(e.target.files?.[0])}
                          data-testid="input-file"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={uploadForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter file name" data-testid="input-name" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={uploadForm.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-category">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                          <SelectItem value="Documents">Documents</SelectItem>
                          <SelectItem value="Images">Images</SelectItem>
                          <SelectItem value="Videos">Videos</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={uploadForm.control}
                  name="tags"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tags (comma-separated)</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="tag1, tag2, tag3" data-testid="input-tags" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button type="submit" disabled={createMediaMutation.isPending} data-testid="button-upload">
                <Upload className="w-4 h-4 mr-2" />
                {createMediaMutation.isPending ? "Uploading..." : "Upload"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <Card data-testid="card-filters">
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="input-search"
              />
            </div>
            <Select value={fileTypeFilter} onValueChange={setFileTypeFilter}>
              <SelectTrigger data-testid="select-filetype-filter">
                <SelectValue placeholder="File Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="jpeg">JPEG</SelectItem>
                <SelectItem value="jpg">JPG</SelectItem>
                <SelectItem value="png">PNG</SelectItem>
                <SelectItem value="pdf">PDF</SelectItem>
                <SelectItem value="mpeg">MPEG</SelectItem>
                <SelectItem value="mp4">MP4</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="select-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Filter by tag"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              data-testid="input-tag-filter"
            />
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start" data-testid="button-date-from">
                  <CalendarIcon className="mr-2 w-4 h-4" />
                  {dateFrom ? format(dateFrom, "PP") : "From Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} />
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start" data-testid="button-date-to">
                  <CalendarIcon className="mr-2 w-4 h-4" />
                  {dateTo ? format(dateTo, "PP") : "To Date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar mode="single" selected={dateTo} onSelect={setDateTo} />
              </PopoverContent>
            </Popover>
          </div>
          {(searchTerm || fileTypeFilter !== "all" || categoryFilter !== "all" || tagFilter || dateFrom || dateTo) && (
            <div className="mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setSearchTerm("");
                  setFileTypeFilter("all");
                  setCategoryFilter("all");
                  setTagFilter("");
                  setDateFrom(undefined);
                  setDateTo(undefined);
                }}
                data-testid="button-clear-filters"
              >
                <X className="w-4 h-4 mr-2" />
                Clear Filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Media List/Grid with Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="tabs-media">
        <TabsList className="mb-4" data-testid="tabs-list-media">
          <TabsTrigger value="my-files" data-testid="tab-my-files">My Files</TabsTrigger>
          <TabsTrigger value="shared-with-me" data-testid="tab-shared-with-me">Shared with me</TabsTrigger>
        </TabsList>

        <TabsContent value="my-files" data-testid="tab-content-my-files">
          <div data-testid="container-media-list">
            {isLoading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-muted-foreground" data-testid="text-loading">Loading media files...</p>
            </CardContent>
          </Card>
        ) : isError ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Film className="w-12 h-12 mx-auto text-destructive mb-4" />
              <p className="text-destructive mb-2" data-testid="text-error">Failed to load media files</p>
              <p className="text-sm text-muted-foreground mb-4">
                {error?.message || "Unable to fetch media files. Please try again."}
              </p>
              <Button variant="outline" onClick={() => refetch()} data-testid="button-retry">
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : filteredMedia.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Film className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4" data-testid="text-no-media">No media files found</p>
              <p className="text-sm text-muted-foreground">Upload files using the form above to get started.</p>
            </CardContent>
          </Card>
        ) : viewMode === "list" ? (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="table-media-list">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3 text-left">Preview</th>
                      <th className="p-3 text-left">Name</th>
                      <th className="p-3 text-left">Type</th>
                      <th className="p-3 text-left">Size</th>
                      <th className="p-3 text-left">Category</th>
                      <th className="p-3 text-left">Tags</th>
                      <th className="p-3 text-left">Uploaded</th>
                      <th className="p-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMedia.map((media) => (
                      <tr key={media.id} className="border-t" data-testid={`row-media-${media.id}`}>
                        <td className="p-3">
                          <MediaThumbnail 
                            mediaId={media.id}
                            fileType={media.fileType}
                            name={media.name}
                          />
                        </td>
                        <td className="p-3 font-medium" data-testid={`text-name-${media.id}`}>{media.name}</td>
                        <td className="p-3 uppercase text-sm" data-testid={`text-type-${media.id}`}>{media.fileType}</td>
                        <td className="p-3 text-sm text-muted-foreground" data-testid={`text-size-${media.id}`}>
                          {formatFileSize(media.fileSize)}
                        </td>
                        <td className="p-3" data-testid={`text-category-${media.id}`}>
                          <Badge variant="outline">{media.category}</Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1 flex-wrap">
                            {media.tags.map((tag, idx) => (
                              <Badge key={idx} variant="secondary" data-testid={`badge-tag-${media.id}-${idx}`}>
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground" data-testid={`text-date-${media.id}`}>
                          {format(new Date(media.createdAt), "PP")}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => openEditDialog(media)}
                              data-testid={`button-edit-${media.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => setSharingMedia(media)}
                              data-testid={`button-share-${media.id}`}
                            >
                              <Share2 className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => setDeletingMediaId(media.id)}
                              data-testid={`button-delete-${media.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="grid-media-cards">
            {filteredMedia.map((media) => (
              <Card key={media.id} data-testid={`card-media-${media.id}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="aspect-video bg-muted rounded flex items-center justify-center overflow-hidden">
                    <MediaThumbnail 
                      mediaId={media.id}
                      fileType={media.fileType}
                      name={media.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold truncate" data-testid={`text-card-name-${media.id}`}>{media.name}</h3>
                    <p className="text-sm text-muted-foreground" data-testid={`text-card-type-${media.id}`}>
                      {media.fileType.toUpperCase()} · {formatFileSize(media.fileSize)}
                    </p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Badge variant="outline" data-testid={`badge-card-category-${media.id}`}>{media.category}</Badge>
                    {media.goalId && <LinkIcon className="w-3 h-3 text-muted-foreground" />}
                  </div>
                  {media.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {media.tags.map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(media.createdAt), "PP")}
                  </p>
                  <div className="flex gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1" 
                      onClick={() => openEditDialog(media)}
                      data-testid={`button-card-edit-${media.id}`}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setSharingMedia(media)}
                      data-testid={`button-card-share-${media.id}`}
                    >
                      <Share2 className="w-3 h-3" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setDeletingMediaId(media.id)}
                      data-testid={`button-card-delete-${media.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </div>
        </TabsContent>

        <TabsContent value="shared-with-me" data-testid="tab-content-shared-with-me">
          <div data-testid="container-shared-media">
            {isLoadingShared ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="animate-spin w-12 h-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                  <p className="text-muted-foreground" data-testid="text-loading-shared">Loading shared files...</p>
                </CardContent>
              </Card>
            ) : sharedWithMe.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Share2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground" data-testid="text-no-shared">No files have been shared with you</p>
                  <p className="text-sm text-muted-foreground">When others share files with you, they will appear here.</p>
                </CardContent>
              </Card>
            ) : viewMode === "list" ? (
              <Card>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="table-shared-media-list">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-3 text-left">Preview</th>
                          <th className="p-3 text-left">Name</th>
                          <th className="p-3 text-left">Type</th>
                          <th className="p-3 text-left">Size</th>
                          <th className="p-3 text-left">Category</th>
                          <th className="p-3 text-left">Shared By</th>
                          <th className="p-3 text-left">Uploaded</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sharedWithMe.map((item) => (
                          <tr key={item.share.id} className="border-t" data-testid={`row-shared-media-${item.share.id}`}>
                            <td className="p-3">
                              <MediaThumbnail 
                                mediaId={item.media.id}
                                fileType={item.media.fileType}
                                name={item.media.name}
                              />
                            </td>
                            <td className="p-3 font-medium" data-testid={`text-shared-name-${item.share.id}`}>{item.media.name}</td>
                            <td className="p-3 uppercase text-sm" data-testid={`text-shared-type-${item.share.id}`}>{item.media.fileType}</td>
                            <td className="p-3 text-sm text-muted-foreground" data-testid={`text-shared-size-${item.share.id}`}>
                              {formatFileSize(item.media.fileSize)}
                            </td>
                            <td className="p-3" data-testid={`text-shared-category-${item.share.id}`}>
                              <Badge variant="outline">{item.media.category}</Badge>
                            </td>
                            <td className="p-3" data-testid={`text-shared-owner-${item.share.id}`}>
                              <div className="flex items-center gap-2">
                                <UserIcon className="w-4 h-4 text-muted-foreground" />
                                <span>{item.owner.firstName || item.owner.email || item.owner.username || "Unknown"}</span>
                              </div>
                            </td>
                            <td className="p-3 text-sm text-muted-foreground" data-testid={`text-shared-date-${item.share.id}`}>
                              {format(new Date(item.media.createdAt), "PP")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="grid-shared-media-cards">
                {sharedWithMe.map((item) => (
                  <Card key={item.share.id} data-testid={`card-shared-media-${item.share.id}`}>
                    <CardContent className="p-4 space-y-3">
                      <div className="aspect-video bg-muted rounded flex items-center justify-center overflow-hidden">
                        <MediaThumbnail 
                          mediaId={item.media.id}
                          fileType={item.media.fileType}
                          name={item.media.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold truncate" data-testid={`text-shared-card-name-${item.share.id}`}>{item.media.name}</h3>
                        <p className="text-sm text-muted-foreground" data-testid={`text-shared-card-type-${item.share.id}`}>
                          {item.media.fileType.toUpperCase()} · {formatFileSize(item.media.fileSize)}
                        </p>
                      </div>
                      <div className="flex gap-2 items-center">
                        <Badge variant="outline">{item.media.category}</Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid={`text-shared-card-owner-${item.share.id}`}>
                        <UserIcon className="w-4 h-4" />
                        <span>Shared by {item.owner.firstName || item.owner.email || item.owner.username || "Unknown"}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(item.media.createdAt), "PP")}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Share Media Dialog */}
      {sharingMedia && (
        <ShareDialog
          isOpen={!!sharingMedia}
          onClose={() => setSharingMedia(null)}
          itemId={sharingMedia.id}
          itemType="media"
          itemTitle={sharingMedia.name}
        />
      )}

      {/* Edit Dialog */}
      <Dialog open={editingMedia !== null} onOpenChange={(open) => !open && setEditingMedia(null)}>
        <DialogContent data-testid="dialog-edit-media" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Media File</DialogTitle>
            <DialogDescription>Update media file details and connect to a goal</DialogDescription>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
              <FormField
                control={editForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter file name" data-testid="input-edit-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-edit-category">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                        <SelectItem value="Documents">Documents</SelectItem>
                        <SelectItem value="Images">Images</SelectItem>
                        <SelectItem value="Videos">Videos</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="tags"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tags (comma-separated)</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="tag1, tag2, tag3" data-testid="input-edit-tags" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="goalId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Connect to Goal (optional)</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || undefined}>
                      <FormControl>
                        <SelectTrigger data-testid="select-edit-goal">
                          <SelectValue placeholder="Select a goal" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">No Goal</SelectItem>
                        {goals.filter(goal => !isFutureGoal(goal)).map((goal) => (
                          <SelectItem key={goal.id} value={goal.id}>{goal.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditingMedia(null)} data-testid="button-cancel-edit">
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMediaMutation.isPending} data-testid="button-save-edit">
                  {updateMediaMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deletingMediaId !== null} onOpenChange={(open) => !open && setDeletingMediaId(null)}>
        <AlertDialogContent data-testid="dialog-delete-confirm" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Media File</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this media file? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletingMediaId && deleteMediaMutation.mutate(deletingMediaId)}
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Storage Warning Dialog */}
      <Dialog open={isStorageWarningOpen} onOpenChange={setIsStorageWarningOpen}>
        <DialogContent data-testid="dialog-storage-warning" className="w-screen h-screen m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <OctagonAlert className="w-6 h-6 text-red-600" />
              Storage Limits
            </DialogTitle>
          </DialogHeader>
          <DialogDescription className="text-base">
            <p>
              Media Storage is Limited by Your Plan, excess data gets charged - for free unlimited data storage use the DRIVE feature to connect to your Google Drive for Free
            </p>
          </DialogDescription>
          <DialogFooter>
            <Button onClick={() => setIsStorageWarningOpen(false)} data-testid="button-close-warning">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
